import { useState, type FormEvent } from "react";
import {
  createCollection,
  createSchema,
  createTemplate,
  addCollectionAuth,
  addNotifyAccount,
  mintFromTemplate,
} from "../api/actions";
import { toStringAttributeMap } from "../utils/attributes";
import { useWallet } from "../wallet/WalletContext";
import { contracts } from "../config";
import { ImageUploadField } from "../components/ImageUploadField";
import { showSuccess, showError } from "../components/Dialog";

/**
 * Plain AtomicAssets creation tools — collection, schema, template — plus
 * the two authorization actions that let escrowbacker and/or nftdrops mint
 * into a collection. None of this is specific to backing/drops; it's just
 * a UI over the standard atomicassets actions so you don't need cleos for
 * the setup steps described in the escrowbacker/nftdrops READMEs.
 */
export function Create() {
  const { session, connect, connecting } = useWallet();
  const [busy, setBusy] = useState<string | null>(null);

  // collection
  const [collectionName, setCollectionName] = useState("");
  const [colName, setColName] = useState("");
  const [colDescription, setColDescription] = useState("");
  const [colImg, setColImg] = useState("");
  const [marketFee, setMarketFee] = useState("0.05");

  // schema
  const [schemaCollection, setSchemaCollection] = useState("");
  const [schemaName, setSchemaName] = useState("");

  // template
  const [templCollection, setTemplCollection] = useState("");
  const [templSchema, setTemplSchema] = useState("");
  const [templName, setTemplName] = useState("");
  const [templDescription, setTemplDescription] = useState("");
  const [templImg, setTemplImg] = useState("");
  const [maxSupply, setMaxSupply] = useState("0");
  const [transferable, setTransferable] = useState(true);
  const [burnable, setBurnable] = useState(true);

  // authorize
  const [authCollection, setAuthCollection] = useState("");
  const [authAccount, setAuthAccount] = useState(contracts.escrow);

  // mint from template
  const [mintCollection, setMintCollection] = useState("");
  const [mintSchema, setMintSchema] = useState("");
  const [mintTemplateId, setMintTemplateId] = useState("");
  const [mintOwner, setMintOwner] = useState("");
  const [mintQuantity, setMintQuantity] = useState("1");
  const [mintProgress, setMintProgress] = useState<string | null>(null);

  async function ensureSession() {
    if (session) return session;
    await connect();
    return undefined;
  }

  async function handleCreateCollection(e: FormEvent) {
    e.preventDefault();
    const s = await ensureSession();
    if (!s) return;
    setBusy("collection");
    try {
      await createCollection(s, {
        collectionName,
        allowNotify: true,
        authorizedAccounts: [s.actor.toString()],
        notifyAccounts: [],
        marketFee: Number(marketFee),
        data: toStringAttributeMap({ name: colName, description: colDescription, img: colImg }),
      });
      showSuccess(`Collection "${collectionName}" created.`);
    } catch (err) {
      showError(err, "Failed to create collection");
    } finally {
      setBusy(null);
    }
  }

  async function handleCreateSchema(e: FormEvent) {
    e.preventDefault();
    const s = await ensureSession();
    if (!s) return;
    setBusy("schema");
    try {
      await createSchema(s, {
        collectionName: schemaCollection,
        schemaName,
        format: [
          { name: "name", type: "string" },
          { name: "img", type: "image" },
          { name: "description", type: "string" },
        ],
      });
      showSuccess(`Schema "${schemaName}" created with a name/img/description format.`);
    } catch (err) {
      showError(err, "Failed to create schema");
    } finally {
      setBusy(null);
    }
  }

  async function handleCreateTemplate(e: FormEvent) {
    e.preventDefault();
    const s = await ensureSession();
    if (!s) return;
    setBusy("template");
    try {
      await createTemplate(s, {
        collectionName: templCollection,
        schemaName: templSchema,
        transferable,
        burnable,
        maxSupply: Number(maxSupply),
        immutableData: toStringAttributeMap({ name: templName, description: templDescription, img: templImg }),
      });
      showSuccess("Template created — check the transaction trace (or AtomicHub) for its template_id.");
    } catch (err) {
      showError(err, "Failed to create template");
    } finally {
      setBusy(null);
    }
  }

  async function handleAuthorize(action: "mint" | "notify") {
    const s = await ensureSession();
    if (!s) return;
    setBusy(action);
    try {
      if (action === "mint") {
        await addCollectionAuth(s, { collectionName: authCollection, accountToAdd: authAccount });
      } else {
        await addNotifyAccount(s, { collectionName: authCollection, accountToAdd: authAccount });
      }
      showSuccess("Done.");
    } catch (err) {
      showError(err, "Failed");
    } finally {
      setBusy(null);
    }
  }

  async function handleMintFromTemplate(e: FormEvent) {
    e.preventDefault();
    const s = await ensureSession();
    if (!s) return;
    const actor = s.actor.toString();
    const owner = mintOwner.trim() || actor;
    const templateId = Number(mintTemplateId);
    const totalQuantity = Math.max(1, Math.floor(Number(mintQuantity) || 1));

    setBusy("mintFromTemplate");
    setMintProgress(null);
    try {
      let minted = 0;
      // mintFromTemplate caps each transaction at 40 actions, so a bigger
      // run needs several signed transactions in a row — looped here so
      // one "quantity" number on the form is all the creator has to think
      // about, rather than doing the batching math themselves.
      while (minted < totalQuantity) {
        const batchSize = Math.min(40, totalQuantity - minted);
        setMintProgress(`Minting ${minted + 1}–${minted + batchSize} of ${totalQuantity}…`);
        await mintFromTemplate(s, {
          collectionName: mintCollection,
          schemaName: mintSchema,
          templateId,
          owner,
          quantity: batchSize,
        });
        minted += batchSize;
      }
      setMintProgress(null);
      showSuccess(`Minted ${totalQuantity} cop${totalQuantity === 1 ? "y" : "ies"} of template ${templateId} to ${owner}.`);
    } catch (err) {
      setMintProgress(null);
      showError(err, "Minting failed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page page--narrow">
      <div className="page__header">
        <h1>Advanced Create</h1>
        <p className="page__subtitle">
          Full manual control over collection → schema → template — the same three steps you'd otherwise run
          through cleos or AtomicHub, useful for reusing one schema/template across many similar NFTs (e.g. a
          game's item set) or picking your own collection name. For a single custom NFT in one step, see{" "}
          <a href="/create">Create an NFT</a> instead.
        </p>
      </div>

      <form className="mint-form" onSubmit={handleCreateCollection}>
        <h2>1. Create a collection</h2>
        <label>
          Collection name (12 chars, a-z 1-5)
          <input value={collectionName} onChange={(e) => setCollectionName(e.target.value)} required />
        </label>
        <label>
          Display name
          <input value={colName} onChange={(e) => setColName(e.target.value)} required />
        </label>
        <label>
          Description
          <input value={colDescription} onChange={(e) => setColDescription(e.target.value)} />
        </label>
        <ImageUploadField label="Image" value={colImg} onChange={setColImg} />
        <label>
          Market fee (0–1, e.g. 0.05 = 5%)
          <input type="number" min="0" max="1" step="0.01" value={marketFee} onChange={(e) => setMarketFee(e.target.value)} />
        </label>
        <button className="btn btn--primary" type="submit" disabled={busy === "collection"}>
          {busy === "collection" ? "Creating…" : "Create collection"}
        </button>
      </form>

      <form className="mint-form" onSubmit={handleCreateSchema}>
        <h2>2. Create a schema</h2>
        <label>
          Collection name
          <input value={schemaCollection} onChange={(e) => setSchemaCollection(e.target.value)} required />
        </label>
        <label>
          Schema name
          <input value={schemaName} onChange={(e) => setSchemaName(e.target.value)} required />
        </label>
        <p className="page__subtitle">Uses a fixed name/img/description format — enough for most collections.</p>
        <button className="btn btn--primary" type="submit" disabled={busy === "schema"}>
          {busy === "schema" ? "Creating…" : "Create schema"}
        </button>
      </form>

      <form className="mint-form" onSubmit={handleCreateTemplate}>
        <h2>3. Create a template</h2>
        <label>
          Collection name
          <input value={templCollection} onChange={(e) => setTemplCollection(e.target.value)} required />
        </label>
        <label>
          Schema name
          <input value={templSchema} onChange={(e) => setTemplSchema(e.target.value)} required />
        </label>
        <label>
          Name
          <input value={templName} onChange={(e) => setTemplName(e.target.value)} required />
        </label>
        <label>
          Description
          <input value={templDescription} onChange={(e) => setTemplDescription(e.target.value)} />
        </label>
        <ImageUploadField label="Image" value={templImg} onChange={setTemplImg} />
        <label>
          Max supply (0 = unlimited)
          <input type="number" min="0" value={maxSupply} onChange={(e) => setMaxSupply(e.target.value)} />
        </label>
        <label>
          <input type="checkbox" checked={transferable} onChange={(e) => setTransferable(e.target.checked)} /> Transferable
        </label>
        <label>
          <input type="checkbox" checked={burnable} onChange={(e) => setBurnable(e.target.checked)} /> Burnable
          (required if you'll ever register this as a backed template — redemption is burn-to-release)
        </label>
        <button className="btn btn--primary" type="submit" disabled={busy === "template"}>
          {busy === "template" ? "Creating…" : "Create template"}
        </button>
      </form>

      <form className="mint-form" onSubmit={handleMintFromTemplate}>
        <h2>4. Mint copies from a template</h2>
        <p className="page__subtitle">
          Step 3 registers a template — it doesn't mint any actual owned copies yet. This does: pick the
          template_id you got back from creating it (check the transaction on AtomicHub, or the "Backing rates" /
          collection page if unsure) and how many copies to mint.
        </p>
        <label>
          Collection name
          <input value={mintCollection} onChange={(e) => setMintCollection(e.target.value)} required />
        </label>
        <label>
          Schema name
          <input value={mintSchema} onChange={(e) => setMintSchema(e.target.value)} required />
        </label>
        <label>
          Template ID
          <input
            type="number"
            min="1"
            value={mintTemplateId}
            onChange={(e) => setMintTemplateId(e.target.value)}
            required
          />
        </label>
        <label>
          Owner (blank = mint to yourself)
          <input value={mintOwner} onChange={(e) => setMintOwner(e.target.value)} placeholder="your account" />
        </label>
        <label>
          Quantity
          <input
            type="number"
            min="1"
            value={mintQuantity}
            onChange={(e) => setMintQuantity(e.target.value)}
            required
          />
        </label>
        <p className="page__subtitle">
          More than 40 copies mints in several signed transactions, one wallet approval per batch of 40 — that's a
          transaction-size limit, not a max_supply limit, so a bigger run is fine, just expect more than one
          approval popup.
        </p>
        <button className="btn btn--primary" type="submit" disabled={busy === "mintFromTemplate"}>
          {mintProgress ?? (busy === "mintFromTemplate" ? "Minting…" : "Mint")}
        </button>
      </form>

      <form className="mint-form" onSubmit={(e) => e.preventDefault()}>
        <h2>5. Authorize a contract on your collection</h2>
        <p className="page__subtitle">
          Grant mint authority (needed by escrowbacker for backed mints, and by nftdrops for plain drops) or
          notification rights (needed by escrowbacker only, so it hears about mints/burns).
        </p>
        <label>
          Collection name
          <input value={authCollection} onChange={(e) => setAuthCollection(e.target.value)} required />
        </label>
        <label>
          Account to authorize
          <input value={authAccount} onChange={(e) => setAuthAccount(e.target.value)} required />
        </label>
        <div className="mint-form__amount-row">
          <button className="btn btn--primary" type="button" onClick={() => handleAuthorize("mint")} disabled={busy === "mint"}>
            {busy === "mint" ? "Working…" : "Grant mint authority"}
          </button>
          <button className="btn btn--outline" type="button" onClick={() => handleAuthorize("notify")} disabled={busy === "notify"}>
            {busy === "notify" ? "Working…" : "Grant notify rights"}
          </button>
        </div>
      </form>
    </div>
  );
}
